<!-- eslint-disable -->
<template>
  <div
    class="crm-card"
    :class="[
      { 'crm-card--selected': isSelected },
      { 'crm-card--compact': boardSettings.density === 'compact' },
      { 'crm-card--aged': isAged }
    ]"
    @click="$emit('click', card)"
  >
    <!-- Top Bar: Bulk Checkbox, Card ID, Priority, Quick Menu -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center gap-1">
        <input
          v-if="bulkMode"
          type="checkbox"
          :checked="isSelected"
          class="crm-card__checkbox mr-1"
          @click.stop="$emit('select', card)"
        />
        <span class="crm-card__id" :title="card.requestCode ? `Internal id: ${card.id}` : null">{{ card.requestCode || card.id }}</span>
      </div>

      <div class="d-flex align-items-center">
        <!-- Expiry Badge for Renewals -->
        <span
          v-if="activePipeline === 'renewals' && card.remainingDays !== undefined"
          class="crm-badge mr-1"
          :class="card.remainingDays <= 7 ? 'crm-badge--danger animate-pulse' : 'crm-badge--warning'"
        >
          <feather-icon icon="ClockIcon" size="10" class="mr-05" />
          {{ card.remainingDays }}d left
        </span>

        <!-- Priority Badge -->
        <span
          v-if="card.priority"
          class="crm-badge mr-1"
          :class="priorityBadgeClass"
        >
          {{ card.priority }}
        </span>

        <!-- Karim, 7 Sep: Reminder / Concern / Needs-Update flags — always red. -->
        <span v-if="card.reminder" class="crm-badge crm-badge--danger mr-1" title="Reminder set">
          <feather-icon icon="BellIcon" size="10" />
        </span>
        <span v-if="hasConcern" class="crm-badge crm-badge--danger mr-1" title="Client has a concern">
          <feather-icon icon="AlertTriangleIcon" size="10" />
        </span>
        <span v-if="card.needsUpdate" class="crm-badge crm-badge--danger mr-1" title="Urgent update requested from sales">
          <feather-icon icon="MessageSquareIcon" size="10" />
        </span>

        <!-- Quick Action Dropdown -->
        <div class="dropdown b-dropdown" @click.stop>
          <button
            type="button"
            class="crm-card__menu-btn"
            @click="toggleMenu"
            title="Card options"
          >
            <feather-icon icon="MoreVerticalIcon" size="14" />
          </button>
          <div v-if="showMenu" class="crm-card__menu shadow-lg">
            <!--
              Karim, 7 Sep: "when the client is new I cannot create activity."
              Root cause: the only way into ActivityModal was the Next Activity
              pill, which only renders once a next activity already exists — a
              brand-new opportunity fresh off a lead (no customer link yet, no
              activity history yet) has no pill and therefore no way to open
              it at all. These two entries are always available regardless.
            -->
            <button type="button" class="crm-card__menu-item" @click="handleAction('schedule-activity')">
              <feather-icon icon="ClockIcon" size="13" class="mr-1" /> Log Activity
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('email')">
              <feather-icon icon="MailIcon" size="13" class="mr-1" /> Send Email
            </button>
            <div class="dropdown-divider my-1"></div>
            <button type="button" class="crm-card__menu-item" @click="handleAction('assign')">
              <feather-icon icon="UserPlusIcon" size="13" class="mr-1" /> Assign Team Member
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('priority')">
              <feather-icon icon="AlertCircleIcon" size="13" class="mr-1" /> Set Priority
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('label')">
              <feather-icon icon="TagIcon" size="13" class="mr-1" /> Add Label
            </button>
            <div class="dropdown-divider my-1"></div>
            <!-- Karim, 7 Sep: Reminder / Concern / Notes quick-actions. -->
            <button type="button" class="crm-card__menu-item" @click="handleAction('reminder')">
              <feather-icon icon="BellIcon" size="13" class="mr-1" />
              {{ card.reminder ? 'Clear Reminder' : 'Set Reminder' }}
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('concern')">
              <feather-icon icon="AlertTriangleIcon" size="13" class="mr-1" />
              {{ hasConcern ? 'Clear Concern' : 'Mark Concern' }}
            </button>
            <button type="button" class="crm-card__menu-item" @click="handleAction('needs-update')">
              <feather-icon icon="MessageSquareIcon" size="13" class="mr-1" />
              {{ card.needsUpdate ? 'Clear Update Request' : 'Request Urgent Update' }}
            </button>
            <div class="dropdown-divider my-1"></div>
            <button type="button" class="crm-card__menu-item text-danger" @click="handleAction('delete')">
              <feather-icon icon="Trash2Icon" size="13" class="mr-1" /> Delete Item
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Title & Client -->
    <h4 class="crm-card__title" :title="card.title">{{ card.title }}</h4>
    <div class="crm-card__client" :title="card.client">
      <feather-icon icon="BriefcaseIcon" size="12" class="mr-1 text-muted" />
      <router-link
        v-if="card.clientId"
        to="/importers"
        class="crm-client-link font-weight-bold text-primary"
        @click.native.stop
      >
        {{ card.client }}
      </router-link>
      <span v-else>{{ card.client }}</span>
    </div>

    <!-- Service scheme + opportunity type -->
    <div v-if="card.serviceScheme || card.type" class="crm-card__meta mt-1 d-flex flex-wrap align-items-center">
      <span v-if="card.serviceScheme" class="crm-badge crm-badge--info mr-1 mb-05" :title="card.serviceScheme">
        <feather-icon icon="AwardIcon" size="10" class="mr-05" />
        {{ card.serviceScheme }}
      </span>
      <span v-if="card.type" class="crm-badge crm-badge--secondary mb-05 text-capitalize">{{ card.type }}</span>
    </div>

    <!--
      Karim, 3 Sep: "5 client, companies and opportunity in pipeline" — same
      last-status concept as the Contacts/Companies lists. Raw Jeem/Saber
      text is the tooltip; the badge is OUR translated version.
    -->
    <div v-if="card.lastRequestStatusLabel" class="crm-card__meta mt-1">
      <span
        class="crm-badge"
        :class="lastStatusBadgeClass"
        :title="`Jeem/Saber: ${card.lastRequestStatus}`"
      >
        <feather-icon icon="FlagIcon" size="10" class="mr-05" />
        {{ card.lastRequestStatusLabel }}
      </span>
    </div>

    <!-- Current step inside the stage (sub-stage) -->
    <div v-if="card.subStage" class="crm-card__substage mt-1">
      <span class="crm-substage-pill" :title="`Current step: ${card.subStage}`">
        <feather-icon icon="CornerDownRightIcon" size="10" class="mr-05" />
        {{ card.subStage }}
      </span>
    </div>

    <!-- Description / Certificates Info -->
    <div v-if="card.description" class="crm-card__description mt-2 text-muted text-sm" style="white-space: pre-wrap; font-size: 0.8rem; line-height: 1.3;">
      <span v-html="formatDescription(card.description)"></span>
    </div>

    <!-- Scheduled Next Activity Pill -->
    <div v-if="card.nextActivity" class="crm-card__next-activity mt-2">
      <div
        class="crm-next-act-pill"
        :class="{ 'crm-next-act-pill--overdue': isNextActivityOverdue }"
        :title="`Next Activity: ${card.nextActivity.type} - ${card.nextActivity.note || ''}`"
        @click.stop="$emit('quick-action', { card, action: 'schedule-activity' })"
      >
        <feather-icon :icon="nextActivityIcon" size="11" class="mr-1" />
        <span class="font-weight-bold mr-1">{{ card.nextActivity.type }}:</span>
        <span>{{ formatNextActivityDate(card.nextActivity.dueDate) }}</span>
        <button type="button" class="crm-next-act-pill__check ml-1" title="Mark Done & Schedule Next" @click.stop="$emit('quick-action', { card, action: 'schedule-activity' })">
          <feather-icon icon="CheckIcon" size="10" />
        </button>
      </div>
    </div>

    <!-- Labels Row -->
    <div v-if="boardSettings.showLabels && card.labels && card.labels.length" class="crm-card__labels mt-2">
      <span
        v-for="(label, idx) in card.labels.slice(0, 4)"
        :key="idx"
        class="crm-label-pill mr-1 mb-1"
        :style="{ backgroundColor: `${label.color}22`, color: label.color, borderColor: `${label.color}44` }"
      >
        {{ label.name }}
      </span>
      <span v-if="card.labels.length > 4" class="crm-label-pill crm-label-pill--more">
        +{{ card.labels.length - 4 }}
      </span>
    </div>

    <!-- Decision Badge for Renewals -->
    <div v-if="card.decision" class="crm-card__decision mt-2">
      <div
        class="crm-badge w-100 text-center"
        :class="card.decision.status === 'renewed' ? 'crm-badge--success' : 'crm-badge--danger'"
      >
        {{ card.decision.status === 'renewed' ? 'Renewed' : 'Rejected' }}
      </div>
      <div class="crm-card__decision-reason mt-1" :title="card.decision.reason">
        Reason: {{ card.decision.reason }}
      </div>
    </div>

    <!-- Sub-indicators: Due Date (MID-CRM P1.5 — the checklist progress bar was
         removed; it read card.checklist, which nothing stores) -->
    <div v-if="hasSubIndicators" class="d-flex align-items-center flex-wrap gap-2 mt-2 pt-1 border-top-subtle">
      <!-- Due Date Badge -->
      <div
        v-if="boardSettings.showDueDates && card.dueDate"
        class="crm-card__due-date"
        :class="{ 'crm-card__due-date--overdue': isOverdue }"
        :title="isOverdue ? 'Overdue!' : 'Due Date'"
      >
        <feather-icon icon="CalendarIcon" size="11" class="mr-05" />
        <span>{{ formatDate(card.dueDate) }}</span>
      </div>
    </div>

    <!-- Footer: Value/Date & Assignee Avatars -->
    <div class="crm-card__footer d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
      <div>
        <div v-if="activePipeline !== 'tickets' && boardSettings.showValues && card.value > 0" class="crm-card__value">
          <!-- The currency is the card's own. Price-list figures come back in SAR
               for KSA schemes, so a hardcoded "AED" labelled SAR money as dirhams. -->
          {{ card.currency || 'AED' }} {{ formatCurrency(card.value) }}
        </div>
        <div v-else-if="activePipeline === 'tickets'" class="crm-card__date">
          {{ card.date || 'N/A' }}
        </div>
      </div>

      <!--
        MID-CRM P2.3 — the responsible salesperson, on the card face.
        A card has exactly ONE owner (`owner_id`); there is no assignees table, so
        this shows the owner and says so plainly when nobody owns it — an
        unassigned opportunity is the thing a manager most needs to spot.
      -->
      <div v-if="boardSettings.showAssignees" class="crm-card__assignees d-flex align-items-center">
        <template v-if="card.owner">
          <div
            class="crm-avatar-sm"
            :title="`Salesperson: ${card.owner}`"
            :style="{ backgroundColor: avatarColor(card.owner) }"
          >
            {{ initials(card.owner) }}
          </div>
          <span class="crm-card__owner-name">{{ card.owner }}</span>
        </template>
        <span v-else class="crm-card__owner-none" title="No salesperson is responsible for this opportunity">
          <feather-icon icon="UserXIcon" size="12" />
          Unassigned
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment'

export default {
  name: 'KanbanCard',
  props: {
    card: { type: Object, required: true },
    activePipeline: { type: String, required: true },
    boardSettings: { type: Object, required: true },
    bulkMode: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false }
  },
  data() {
    return {
      showMenu: false
    }
  },
  computed: {
    hasConcern() {
      return !!(this.card.labels && this.card.labels.some(l => l.name === 'Concern'))
    },
    priorityBadgeClass() {
      if (this.card.priority === 'High') return 'crm-badge--danger'
      if (this.card.priority === 'Medium') return 'crm-badge--warning'
      return 'crm-badge--primary'
    },
    lastStatusBadgeClass() {
      const map = { issued: 'crm-badge--success', rejected: 'crm-badge--danger', in_progress: 'crm-badge--warning' }
      return map[this.card.lastRequestStatusNormalized] || 'crm-badge--secondary'
    },
    // MID-CRM P1.3 — `assigneesList` removed. It read `card.assignees`, a field no
    // endpoint ever returns because there is no assignees table; the card face now
    // renders `card.owner` directly (the one real owner).
    // MID-CRM P1.5 — checklistTotal / checklistDone removed with the checklist
    // feature: `card.checklist` is not a column and no endpoint returns it, so the
    // progress bar on the card face could only ever read 0/0.
    hasSubIndicators() {
      return (this.boardSettings.showDueDates && this.card.dueDate)
    },
    isOverdue() {
      if (!this.card.dueDate) return false
      return moment(this.card.dueDate).isBefore(moment(), 'day')
    },
    nextActivityIcon() {
      if (!this.card.nextActivity) return 'CalendarIcon'
      const type = this.card.nextActivity.type
      const map = { Call: 'PhoneIcon', Email: 'MailIcon', Visit: 'MapPinIcon', Meeting: 'UsersIcon', 'Follow-up': 'ClockIcon' }
      return map[type] || 'CalendarIcon'
    },
    isNextActivityOverdue() {
      if (!this.card.nextActivity || !this.card.nextActivity.dueDate) return false
      return moment(this.card.nextActivity.dueDate).isBefore(moment(), 'day')
    },
    isAged() {
      if (!this.boardSettings.cardAging || !this.card.stageChangedAt) return false
      const daysInStage = moment().diff(moment(this.card.stageChangedAt), 'days')
      return daysInStage >= (this.boardSettings.cardAgingDays || 14)
    }
  },
  mounted() {
    document.addEventListener('click', this.closeMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
  },
  methods: {
    formatDescription(text) {
      if (!text) return ''
      // SECURITY (audit F-4): escape HTML first — description is user input
      // rendered via v-html, i.e. a stored-XSS vector without this.
      const escaped = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
      // Then convert markdown-like bold to <strong>
      return escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    },
    toggleMenu() {
      this.showMenu = !this.showMenu
    },
    closeMenu() {
      this.showMenu = false
    },
    handleAction(action) {
      this.showMenu = false
      this.$emit('quick-action', { card: this.card, action })
    },
    formatCurrency(val) {
      if (!val) return '0'
      return Number(val).toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('MMM D')
    },
    formatNextActivityDate(dateStr) {
      if (!dateStr) return 'Scheduled'
      const m = moment(dateStr)
      if (m.isSame(moment(), 'day')) return 'Today'
      if (m.isSame(moment().add(1, 'day'), 'day')) return 'Tomorrow'
      return m.format('MMM D')
    },
    initials(name) {
      if (!name) return 'U'
      const parts = name.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.slice(0, 2).toUpperCase()
    },
    avatarColor(name) {
      const colors = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#a855f7']
      let hash = 0
      for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i)
      return colors[Math.abs(hash) % colors.length]
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: #6366f1;
$success: #10b981;
$warning: #f59e0b;
$danger: #f43f5e;
$slate-50: #f8fafc;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-300: #cbd5e1;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-700: #334155;
$slate-900: #0f172a;

.crm-card {
  background-color: #ffffff;
  border: 1px solid $slate-200;
  border-radius: 10px;
  padding: 0.9rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-2px);
    border-color: $primary;
    box-shadow: 0 4px 14px rgba($slate-900, 0.08);
  }

  &--selected {
    border-color: $primary;
    background-color: rgba($primary, 0.02);
    box-shadow: 0 0 0 2px rgba($primary, 0.2);
  }

  &--compact {
    padding: 0.6rem;

    .crm-card__title {
      font-size: 0.82rem;
    }
  }

  &--aged {
    opacity: 0.82;
    border-left: 3px solid $warning;
    background-color: lighten($warning, 47%);
  }

  .crm-substage-pill {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.12rem 0.45rem;
    border-radius: 10px;
    color: #6b4ed8;
    background-color: rgba(115, 103, 240, 0.12);
    border: 1px dashed rgba(115, 103, 240, 0.45);
  }

  .mb-05 { margin-bottom: 0.25rem; }

  &__id {
    font-family: monospace;
    font-size: 0.72rem;
    font-weight: 700;
    color: $slate-500;
    background-color: $slate-100;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    border: 1px solid $slate-200;
  }

  &__title {
    font-size: 0.88rem;
    font-weight: 800;
    color: $slate-900;
    line-height: 1.35;
    margin: 0.25rem 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__client {
    font-size: 0.78rem;
    font-weight: 600;
    color: $slate-500;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 0.88rem;
    font-weight: 900;
    color: $success;
  }

  &__date {
    font-size: 0.72rem;
    font-weight: 600;
    color: $slate-400;
  }

  &__menu-btn {
    border: none;
    background: transparent;
    color: $slate-400;
    padding: 0.15rem 0.3rem;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: $slate-100;
      color: $slate-700;
    }
  }

  &__menu {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 100;
    background: #ffffff;
    border: 1px solid $slate-200;
    border-radius: 8px;
    padding: 0.35rem 0;
    min-width: 170px;
  }

  &__menu-item {
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    padding: 0.4rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: $slate-700;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
      background-color: $slate-50;
    }
  }

  &__due-date {
    font-size: 0.72rem;
    font-weight: 700;
    color: $slate-500;
    background-color: $slate-100;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;

    &--overdue {
      background-color: rgba($danger, 0.1);
      color: $danger;
    }
  }
}

.crm-label-pill {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  border: 1px solid transparent;

  &--more {
    background-color: $slate-100;
    color: $slate-500;
  }
}

.crm-card__owner-name {
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  margin-left: 6px;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crm-card__owner-none {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  color: #f59e0b;
}

.crm-avatar-sm {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 0.65rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #ffffff;

  &--more {
    background-color: $slate-500;
  }
}

.crm-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 0.15rem 0.45rem;
  border-radius: 20px;

  &--primary { background: rgba($primary, 0.1); color: $primary; }
  &--warning { background: rgba($warning, 0.15); color: darken($warning, 10%); }
  &--success { background: rgba($success, 0.1); color: $success; }
  &--danger { background: rgba($danger, 0.1); color: $danger; }
}

.crm-next-act-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: $primary;
  background-color: rgba($primary, 0.08);
  border: 1px solid rgba($primary, 0.2);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: rgba($primary, 0.15);
    border-color: $primary;
  }

  &--overdue {
    background-color: rgba($danger, 0.1);
    color: $danger;
    border-color: rgba($danger, 0.3);
    animation: pulse 2s infinite;
  }

  &__check {
    border: none;
    background: rgba($primary, 0.2);
    color: $primary;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;

    &:hover {
      background: $primary;
      color: #fff;
    }
  }
}

.mr-05 { margin-right: 0.15rem; }
.mb-05 { margin-bottom: 0.15rem; }
</style>
